import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, Timer, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OvertimePortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [overtimeRecords, setOvertimeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totalStats, setTotalStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    totalApprovedHours: '00:00:00'
  });

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    reason: '',
    approval_status: 'approved'
  });

  useEffect(() => {
    fetchOvertimeData();
  }, [impersonatedUser]);

  const fetchOvertimeData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(
        `${API}/superadmin/impersonate/overtime/${impersonatedUser.employee_id}`,
        config
      );
      
      const records = response.data.records || [];
      setOvertimeRecords(records);
      
      // Calculate stats
      const pending = records.filter(r => r.approval_status === 'pending').length;
      const approved = records.filter(r => r.approval_status === 'approved').length;
      
      // Calculate total approved hours
      let totalSeconds = 0;
      records.filter(r => r.approval_status === 'approved').forEach(record => {
        const [hours, minutes, seconds] = (record.total_overtime || '00:00:00').split(':').map(Number);
        totalSeconds += hours * 3600 + minutes * 60 + seconds;
      });
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      
      setTotalStats({
        total: records.length,
        pending,
        approved,
        totalApprovedHours: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      });
    } catch (error) {
      console.error('Error fetching overtime data:', error);
      toast.error('Unable to fetch overtime data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const calculateOvertimeHours = (startDate, startTime, endDate, endTime) => {
    if (!startDate || !startTime || !endDate || !endTime) return '00:00:00';
    
    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);
    const diffMs = end - start;
    
    if (diffMs < 0) return '00:00:00';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleOpenCreate = () => {
    setEditingRecord(null);
    setFormData({
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
      reason: '',
      approval_status: 'approved'
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      start_date: record.start_date || '',
      start_time: record.start_time || '',
      end_date: record.end_date || '',
      end_time: record.end_time || '',
      reason: record.reason || '',
      approval_status: record.approval_status || 'approved'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.start_date || !formData.start_time || !formData.end_date || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const totalOvertime = calculateOvertimeHours(
        formData.start_date, formData.start_time,
        formData.end_date, formData.end_time
      );

      const payload = {
        portal: 'overtime',
        employee_id: impersonatedUser.employee_id,
        data: {
          ...formData,
          employee_id: impersonatedUser.employee_id,
          employee_name: impersonatedUser.name,
          total_overtime: totalOvertime,
          submitted_at: new Date().toISOString()
        }
      };

      if (editingRecord) {
        await axios.put(`${API}/superadmin/entries/update`, {
          ...payload,
          record_id: editingRecord.id
        }, config);
        toast.success('Overtime record updated');
      } else {
        await axios.post(`${API}/superadmin/entries/create`, payload, config);
        toast.success('Overtime record created');
      }

      setDialogOpen(false);
      fetchOvertimeData();
    } catch (error) {
      console.error('Error saving overtime:', error);
      toast.error(error.response?.data?.detail || 'Failed to save overtime');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm('Are you sure you want to delete this overtime record?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API}/superadmin/entries/delete`, {
        ...config,
        data: {
          portal: 'overtime',
          employee_id: impersonatedUser.employee_id,
          record_id: record.id
        }
      });

      toast.success('Overtime record deleted');
      fetchOvertimeData();
    } catch (error) {
      toast.error('Failed to delete overtime record');
    }
  };

  const handleApprove = async (record) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`${API}/superadmin/entries/update`, {
        portal: 'overtime',
        employee_id: impersonatedUser.employee_id,
        record_id: record.id,
        data: { ...record, approval_status: 'approved' }
      }, config);

      toast.success('Overtime approved');
      fetchOvertimeData();
    } catch (error) {
      toast.error('Failed to approve overtime');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="overtime-portal-embed">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Submissions</p>
                <p className="text-2xl font-bold text-blue-600">{totalStats.total}</p>
              </div>
              <Clock className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-600">{totalStats.pending}</p>
              </div>
              <Timer className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Approved Hours</p>
                <p className="text-2xl font-bold text-green-600">{totalStats.totalApprovedHours}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Overtime Records Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Overtime Records for {impersonatedUser.name}</CardTitle>
            <Button onClick={handleOpenCreate} size="sm" className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-1" /> Add Overtime
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {overtimeRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No overtime records found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Start Date & Time</TableHead>
                  <TableHead>End Date & Time</TableHead>
                  <TableHead>Total Overtime</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overtimeRecords.slice(0, 15).map((record, idx) => (
                  <TableRow key={record.id || idx} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="font-medium">{formatDate(record.start_date)}</div>
                      <div className="text-sm text-gray-500">{record.start_time}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{formatDate(record.end_date)}</div>
                      <div className="text-sm text-gray-500">{record.end_time}</div>
                    </TableCell>
                    <TableCell>
                      <span className="font-bold text-blue-600 text-lg">{record.total_overtime}</span>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate" title={record.reason}>
                      {record.reason || '-'}
                    </TableCell>
                    <TableCell>{getStatusBadge(record.approval_status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {record.approval_status === 'pending' && (
                          <Button size="sm" variant="ghost" onClick={() => handleApprove(record)} title="Approve">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(record)}>
                          <Edit2 className="w-4 h-4 text-blue-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(record)}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Edit Overtime' : 'Add Overtime'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                />
              </div>
            </div>

            {formData.start_date && formData.start_time && formData.end_date && formData.end_time && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-600">Calculated Overtime:</p>
                <p className="text-xl font-bold text-blue-600">
                  {calculateOvertimeHours(formData.start_date, formData.start_time, formData.end_date, formData.end_time)}
                </p>
              </div>
            )}

            <div>
              <Label>Reason</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Enter reason for overtime"
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select value={formData.approval_status} onValueChange={(v) => setFormData({...formData, approval_status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OvertimePortalEmbed;
